// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { Dispatch } from 'react';

import { DEFAULT_LIGHTING, type LightingState } from '../../../viewport/cameraViews';
import { TextLink } from '../../TextLink';
import type { SettingsAction } from './settingsState';
import styles from './ModelSettings.module.css';

const SLIDERS: readonly {
  key: keyof LightingState;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  digits: number;
}[] = [
  { key: 'keyIntensity', label: 'Hovedlys', min: 0, max: 5, step: 0.1, unit: '', digits: 1 },
  { key: 'ambientIntensity', label: 'Fyldlys', min: 0, max: 3, step: 0.1, unit: '', digits: 1 },
  { key: 'azimuth', label: 'Retning', min: -180, max: 180, step: 5, unit: '°', digits: 0 },
  { key: 'elevation', label: 'Højde', min: 0, max: 90, step: 5, unit: '°', digits: 0 },
];

/**
 * Lys: the key light (intensity, bearing, height) and the hemisphere fill,
 * the old viewport's lighting controls (#443). They light the mesh; points
 * carry their own colours.
 */
export function LightSection({ lighting, dispatch }: { lighting: LightingState; dispatch: Dispatch<SettingsAction> }) {
  const changed = SLIDERS.some(({ key }) => lighting[key] !== DEFAULT_LIGHTING[key]);
  return (
    <>
      <div className={`${styles.grid} ${styles.gridAligned}`}>
        {SLIDERS.map((s) => (
          <LightRow key={s.key} spec={s} value={lighting[s.key]} onChange={(v) => dispatch({ type: 'lighting', patch: { [s.key]: v } })} />
        ))}
      </div>
      <div className={styles.row}>
        <span className={styles.note}>Lyser bygningsdelene op; punkterne har egne farver.</span>
        <TextLink size="sm" disabled={!changed} onClick={() => dispatch({ type: 'lighting', patch: null })}>
          Nulstil
        </TextLink>
      </div>
    </>
  );
}

function LightRow({ spec, value, onChange }: { spec: (typeof SLIDERS)[number]; value: number; onChange: (v: number) => void }) {
  return (
    <>
      <span className={styles.label}>{spec.label}</span>
      <span className={styles.slider}>
        <input
          type="range"
          className={styles.range}
          min={spec.min}
          max={spec.max}
          step={spec.step}
          value={value}
          aria-label={spec.label}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span className={styles.sliderValue}>
          {value.toLocaleString('da-DK', { minimumFractionDigits: spec.digits, maximumFractionDigits: spec.digits })}
          {spec.unit}
        </span>
      </span>
    </>
  );
}
